import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";
import { loadPanelMeta, persistPanelBase } from "@/lib/panelMeta";
import { backendUrl, publicApi } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import { MANAGERS } from "@/pages/admin/managerConfig";

const Home = lazy(() => import("@/pages/Home"));
const IpBlocked = lazy(() => import("@/pages/IpBlocked"));
const PayPage = lazy(() => import("@/pages/Pay"));
const ClientPaymentPage = lazy(() => import("@/pages/ClientPayment"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const LoginGate = lazy(() => import("@/pages/admin/LoginGate").then((m) => ({ default: m.default })));
const LegacyAdminRedirect = lazy(() =>
  import("@/pages/admin/LoginGate").then((m) => ({ default: m.LegacyAdminRedirect }))
);
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const ProtectedRoute = lazy(() => import("@/pages/admin/ProtectedRoute"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const Manager = lazy(() => import("@/pages/admin/Manager"));
const SiteContent = lazy(() => import("@/pages/admin/SiteContent"));
const ThemeSettings = lazy(() => import("@/pages/admin/ThemeSettings"));
const SeoSettings = lazy(() => import("@/pages/admin/SeoSettings"));
const MediaLibrary = lazy(() => import("@/pages/admin/MediaLibrary"));
const Analytics = lazy(() => import("@/pages/admin/Analytics"));
const Messages = lazy(() => import("@/pages/admin/Messages"));
const Profile = lazy(() => import("@/pages/admin/Profile"));
const BackupManager = lazy(() => import("@/pages/admin/BackupManager"));
const PaymentSettings = lazy(() => import("@/pages/admin/PaymentSettings"));
const AdminPanelSettings = lazy(() => import("@/pages/admin/AdminPanelSettings"));
const ResumeBuilder = lazy(() => import("@/pages/admin/ResumeBuilder"));
const SecurityLogs = lazy(() => import("@/pages/admin/SecurityLogs"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-10 h-10 rounded-full border-2 border-white/20 border-t-white/80 animate-spin" />
    </div>
  );
}

function LegacySessionStrip({ base }) {
  const { "*": rest } = useParams();
  const to = rest ? `${base}/${rest}` : base;
  return <Navigate to={to} replace />;
}

function AppRouter() {
  const stored = (() => {
    try {
      return localStorage.getItem("panel_base_path") || "";
    } catch {
      return "";
    }
  })();
  const [base, setBase] = useState(stored || "/admin");
  const [clientPayPath, setClientPayPath] = useState("payment");

  useEffect(() => {
    loadPanelMeta(false).then((meta) => {
      const b = meta.base_path || "/admin";
      persistPanelBase(b);
      setBase(b);
    });

    publicApi
      .get("/pay/public/config")
      .then(({ data }) => setClientPayPath(data.url_path || "payment"))
      .catch(() => {});
  }, []);

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path={`/${clientPayPath}`} element={<ClientPaymentPage />} />
        {clientPayPath !== "hamkari" && (
          <Route path="/hamkari" element={<Navigate to={`/${clientPayPath}`} replace />} />
        )}
        <Route path="/pay/:slug" element={<PayPage />} />

        <Route path={`${base}/s/:sid/*`} element={<LegacySessionStrip base={base} />} />
        <Route path={`${base}/s/:sid`} element={<Navigate to={base} replace />} />

        <Route path={base} element={<Outlet />}>
          <Route path="login/*" element={<LoginGate />} />
          <Route path="login" element={<LoginGate />} />
          <Route
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="messages" element={<Messages />} />
            <Route path="content" element={<SiteContent />} />
            <Route path="resume" element={<ResumeBuilder />} />
            <Route path="theme" element={<ThemeSettings />} />
            <Route path="seo" element={<SeoSettings />} />
            <Route path="media" element={<MediaLibrary />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="logs" element={<SecurityLogs />} />
            <Route path="profile" element={<Profile />} />
            <Route path="backup" element={<BackupManager />} />
            <Route path="payments" element={<PaymentSettings />} />
            <Route path="panel-settings" element={<AdminPanelSettings />} />
            {MANAGERS.map((m) => (
              <Route key={m.key} path={m.key} element={<Manager config={m} />} />
            ))}
            <Route path="*" element={<NotFound admin />} />
          </Route>
        </Route>

        {base !== "/admin" && (
          <>
            <Route path="/admin/login/*" element={<LegacyAdminRedirect />} />
            <Route path="/admin/login" element={<LegacyAdminRedirect />} />
            <Route path="/admin/*" element={<Navigate to={base} replace />} />
          </>
        )}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function readInjectedIpStatus() {
  try {
    const d = typeof window !== "undefined" ? window.__IP_STATUS__ : null;
    if (d && typeof d === "object") return d;
  } catch {
    /* ignore */
  }
  return null;
}

function App() {
  const injected = readInjectedIpStatus();
  const [ipBlock, setIpBlock] = useState(injected?.blocked ? injected : null);

  const checkIp = useCallback(() => {
    const apiBase = backendUrl();
    if (!apiBase) return;
    fetch(`${apiBase}/api/security/ip-status`, { mode: "cors", cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.blocked) setIpBlock(d);
        else setIpBlock(null);
      })
      .catch(() => setIpBlock(null));
  }, []);

  useEffect(() => {
    if (injected?.blocked) return undefined;
    if (injected && injected.blocked === false) {
      const t = setTimeout(checkIp, 15000);
      return () => clearTimeout(t);
    }
    checkIp();
    return undefined;
  }, [checkIp, injected]);

  if (ipBlock?.blocked) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <IpBlocked info={ipBlock} onExpired={checkIp} />
      </Suspense>
    );
  }

  return (
    <AuthProvider>
      <SiteProvider>
        <BrowserRouter>
          <SeoHead />
          <Toaster position="top-center" theme="dark" richColors closeButton />
          <AppRouter />
        </BrowserRouter>
      </SiteProvider>
    </AuthProvider>
  );
}

export default App;
